import React from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Users,
} from 'lucide-react';
import type { TeamMemberWorkload } from '../../../modules/minha-mesa/types/workItem';
import {
  MAX_ACTIVE_TASKS_PER_OPERATOR,
  visibleBottleneck,
  getInitials,
  sortMembersByColumn,
  type SortDirection,
  type TeamSortColumn,
} from './teamCapacityShared';
import { OCCUPANCY_BADGE_CLASS, occupancyTier } from '../executive/opsExecutiveShared';
import { operatorLoadLabel, overdueDeadlineLabel } from '../executive/opsTeamMetrics';
import { TeamCapacityActivitiesPanel } from './TeamCapacityActivitiesPanel';
import type { UnifiedWorkItem } from '../../../modules/minha-mesa/types/workItem';

interface TeamCapacityOperatorTableProps {
  members: TeamMemberWorkload[];
  items: UnifiedWorkItem[];
  selectedUserId: string | null;
  onSelectUser: (userId: string | null) => void;
  sortColumn: TeamSortColumn;
  sortDirection: SortDirection;
  onSortChange: (col: TeamSortColumn) => void;
  maxHeight?: string;
}

export const TeamCapacityOperatorTable: React.FC<TeamCapacityOperatorTableProps> = ({
  members,
  items,
  selectedUserId,
  onSelectUser,
  sortColumn,
  sortDirection,
  onSortChange,
  maxHeight = 'max-h-[280px]',
}) => {
  const sorted = sortMembersByColumn(members, sortColumn, sortDirection);
  const selectedMember = members.find((m) => m.user_id === selectedUserId);

  const SortableTh = ({
    col,
    label,
    className,
    align = 'center',
  }: {
    col: TeamSortColumn;
    label: string;
    className?: string;
    align?: 'left' | 'center';
  }) => {
    const active = sortColumn === col;
    const Icon = active ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <th className={`px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider text-slate-800 dark:text-slate-100 ${align === 'center' ? 'text-center' : 'text-left'} ${className ?? ''}`}>
        <button
          type="button"
          onClick={() => onSortChange(col)}
          className={`inline-flex items-center gap-1 ${align === 'center' ? 'justify-center' : ''} w-full select-none transition-colors ${
            active ? 'text-slate-800 dark:text-slate-100' : 'text-[var(--text-muted)] hover:text-slate-800'
          }`}
        >
          <span>{label}</span>
          <Icon size={9} className={active ? 'opacity-90' : 'opacity-40'} />
        </button>
      </th>
    );
  };

  return (
    <div className={`overflow-y-auto overflow-x-auto ${maxHeight === 'h-full' ? 'h-full max-h-full' : maxHeight}`}>
      <table className="min-w-full table-fixed text-[11px]">
        <thead className="sticky top-0 z-[1] bg-[var(--bg-elevated)] text-left">
          <tr className="border-b border-[var(--border-default)]/60">
            <SortableTh col="name" label="Operador" align="left" className="w-[200px]" />
            <SortableTh col="status" label="Carga" className="w-[88px]" />
            <SortableTh col="in_progress" label="Andamento" />
            <SortableTh col="pending" label="Pendentes" />
            <SortableTh col="overdue" label="Atrasadas" />
            <SortableTh col="occupation" label="Ocupação %" className="w-[100px]" />
            <SortableTh col="sla" label="SLA" className="w-[80px]" />
            <SortableTh col="bottleneck" label="Pressão" className="w-[90px]" />
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border-default)]/40">
          {sorted.map((member) => {
            const isSelected = selectedUserId === member.user_id;
            const bn = visibleBottleneck(member);
            const occupationPct = Math.round((member.total_active / MAX_ACTIVE_TASKS_PER_OPERATOR) * 100);
            const loadTier = occupancyTier(occupationPct);
            const loadBadge = OCCUPANCY_BADGE_CLASS[loadTier];
            const overloaded = loadTier === 'critical';
            const occupationBand = occupancyTier(occupationPct);
            const occupationClass = occupationBand === 'critical'
              ? 'text-red-600'
              : occupationBand === 'high'
                ? 'text-amber-600'
                : occupationBand === 'medium'
                  ? 'text-sky-600'
                  : 'text-emerald-600';
            const occupationBar = occupationBand === 'critical'
              ? 'bg-red-500'
              : occupationBand === 'high'
                ? 'bg-amber-400'
                : occupationBand === 'medium'
                  ? 'bg-sky-500'
                  : 'bg-emerald-500';
            const slaOkPct = 100 - member.sla_violated_pct;

            return (
              <React.Fragment key={member.user_id}>
                <tr
                  onClick={() => onSelectUser(isSelected ? null : member.user_id)}
                  className={`cursor-pointer transition-colors ${
                    overloaded && !isSelected ? 'border-l-2 border-l-red-400' : ''
                  } ${isSelected ? 'bg-blue-500/5' : 'hover:bg-[var(--button-hover)]/50'}`}
                >
                  <td className="px-2 py-2 align-middle">
                    <div className="flex items-center gap-2">
                      {isSelected ? <ChevronDown size={11} /> : <ChevronRight size={11} className="text-[var(--text-muted)]" />}
                      <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-[9px] font-bold ${
                        overloaded ? 'bg-red-100 text-red-800 dark:bg-red-500/20' : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
                      }`}>
                        {getInitials(member.user_name)}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-slate-800 dark:text-slate-100">{member.user_name}</div>
                        <div className="truncate text-[9px] text-[var(--text-muted)]">{member.role_label ?? '—'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center align-middle">
                    <span className={`inline-block rounded-sm px-1.5 py-0.5 text-[8px] font-bold ${loadBadge.bg} ${loadBadge.text}`} title="Status de carga: só ocupação simultânea. Atraso não altera esta coluna.">
                      {operatorLoadLabel(occupationPct)}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center align-middle font-mono font-semibold text-blue-600">{member.tasks_in_progress}</td>
                  <td className="px-2 py-2 text-center align-middle font-mono font-semibold text-amber-600">{member.tasks_pending}</td>
                  <td className={`px-2 py-2 text-center align-middle ${member.tasks_overdue > 0 ? 'text-red-600' : 'text-[var(--text-muted)]'}`}>
                    <div className="font-mono font-semibold">{member.tasks_overdue}</div>
                    <div className="text-[8px] font-semibold">{overdueDeadlineLabel(member.tasks_overdue)}</div>
                  </td>
                  <td className="px-2 py-2 align-middle">
                    <div className="flex flex-col items-center gap-0.5">
                      <span className={`text-[10px] font-bold tabular-nums ${occupationClass}`}>{occupationPct}%</span>
                      <div className="h-[3px] w-full max-w-[64px] rounded-sm bg-[var(--bg-elevated)] overflow-hidden">
                        <div
                          className={`h-full rounded-sm ${occupationBar}`}
                          style={{ width: `${Math.min(occupationPct, 100)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center align-middle">
                    {member.sla_violated_pct > 0 ? (
                      <div className="flex flex-col items-center leading-tight">
                        <span className="text-[9px] font-semibold text-emerald-600">{slaOkPct}% OK</span>
                        <span className="text-[8px] font-semibold text-red-600">{member.sla_violated_pct}% viol.</span>
                      </div>
                    ) : (
                      <span className="text-[9px] text-[var(--text-muted)]">--</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center align-middle">
                    <span className={`text-[9px] font-bold ${bn.color}`}>{bn.text}</span>
                  </td>
                </tr>
                {isSelected && selectedMember && (
                  <tr>
                    <td colSpan={8} className="p-0">
                      <TeamCapacityActivitiesPanel
                        userId={member.user_id}
                        member={selectedMember}
                        items={items}
                        onClose={() => onSelectUser(null)}
                        embedded
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={8} className="px-3 py-8 text-center">
                <Users size={18} className="mx-auto mb-1.5 text-[var(--text-muted)] opacity-40" />
                <p className="text-[10px] text-[var(--text-muted)]">Nenhum operador neste setor</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TeamCapacityOperatorTable;
